import { PERMISSIONS, USER_ROLES } from "@constants/user.roles";
import { extractRoleTokens, shouldGrantStaticPrototypePermissions } from "@utils/psdaAdminAccess";
import { permissionListIncludes } from "@utils/permissionIdentifiers";

/** Real calendar event permissions used by Event Management screens. */
export const EVENT_MANAGEMENT_PERMISSIONS = {
	VIEW_EVENT: PERMISSIONS.VIEW_EVENT,
	CREATE_EVENT: PERMISSIONS.CREATE_EVENT,
	EDIT_EVENT: PERMISSIONS.EDIT_EVENT,
	DELETE_EVENT: PERMISSIONS.DELETE_EVENT,
} as const;

/** Any of these grants Event Management module / sidebar visibility. */
export const EVENT_MANAGEMENT_ACCESS_PERMISSIONS = [
	PERMISSIONS.VIEW_EVENT,
	PERMISSIONS.CREATE_EVENT,
	PERMISSIONS.EDIT_EVENT,
	PERMISSIONS.DELETE_EVENT,
] as const;

/** Minimum permissions to open Event Creation. */
export const EVENT_CREATION_ACCESS_PERMISSIONS = EVENT_MANAGEMENT_ACCESS_PERMISSIONS;

/** Minimum permissions to open Events Request / Events Clone screens. */
export const EVENTS_REQUEST_ACCESS_PERMISSIONS = [PERMISSIONS.VIEW_EVENT, PERMISSIONS.EDIT_EVENT] as const;

/** PSDA endorsement workflow (Events Request) — not granted to typical TEVTA/PVTC event creators. */
export const ACADEMIC_CALENDAR_EVENT_WORKFLOW_ACCESS_PERMISSIONS = [
	PERMISSIONS.ACADEMIC_CALENDAR_EVENT_LIST,
	PERMISSIONS.ACADEMIC_CALENDAR_EVENT_READ,
	PERMISSIONS.ACADEMIC_CALENDAR_EVENT_ENDORSE,
	PERMISSIONS.ACADEMIC_CALENDAR_EVENT_REJECT,
] as const;

/** Sidebar / route access for Clone Events — explicit API permission only. */
export const EVENTS_CLONE_ACCESS_PERMISSIONS = [PERMISSIONS.CLONE_CALENDAR_EVENT] as const;

const normalizeRoleToken = (value: string): string =>
	value.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");

export function isSystemOrSuperAdminRole(roles: unknown[] | undefined): boolean {
	return extractRoleTokens(roles).some(role => {
		const normalized = normalizeRoleToken(role);
		return normalized === USER_ROLES.SYSTEM_ADMIN || normalized === USER_ROLES.SUPER_ADMIN;
	});
}

/** Legacy PSDA organization / role gate for endorsement screens. */
export function canAccessEventsRequestAsPsdaAdmin(options: {
	roles?: unknown[];
	organizationTypeId?: number | null;
	userTypeName?: string | null;
}): boolean {
	return shouldGrantStaticPrototypePermissions(options);
}

export function userHasEventsRequestScreenPermissions(userPermissions: string[] | undefined): boolean {
	if (!userPermissions?.length) return false;
	if (permissionListIncludes(userPermissions, PERMISSIONS.ALL_READ)) return true;
	return EVENTS_REQUEST_ACCESS_PERMISSIONS.some(permission => permissionListIncludes(userPermissions, permission));
}

/** Who may see Events Request (not TEVTA-only event creators). */
export function canAccessEventsRequestWorkflow(options: {
	roles?: unknown[];
	organizationTypeId?: number | null;
	userTypeName?: string | null;
	userPermissions?: string[];
}): boolean {
	const { userPermissions = [] } = options;

	if (canAccessEventsRequestAsPsdaAdmin(options)) return true;
	if (isSystemOrSuperAdminRole(options.roles)) return true;
	if (permissionListIncludes(userPermissions, PERMISSIONS.ALL_READ)) return true;
	return ACADEMIC_CALENDAR_EVENT_WORKFLOW_ACCESS_PERMISSIONS.some(permission =>
		permissionListIncludes(userPermissions, permission),
	);
}

export function canAccessEventsRequestModule(options: {
	roles?: unknown[];
	organizationTypeId?: number | null;
	userTypeName?: string | null;
	userPermissions?: string[];
}): boolean {
	return canAccessEventsRequestWorkflow(options) && userHasEventsRequestScreenPermissions(options.userPermissions);
}

/** Uses JWT permissions only — `all.read` must not imply clone access. */
export function canAccessEventsCloneModule(userPermissions: string[] | undefined): boolean {
	if (!userPermissions?.length) return false;
	return EVENTS_CLONE_ACCESS_PERMISSIONS.some(permission => permissionListIncludes(userPermissions, permission));
}
